import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "../pages/Dashboard";
import Users from "../pages/Users";
import Scholars from "../pages/Scholars";
import Bookings from "../pages/Bookings";
import Services from "../pages/Services";
import Finance from "../pages/Finance";
import PayoutsRefunds from "../pages/PayoutsRefunds";
import Advertisements from "../pages/Advertisements";
import Analytics from "../pages/Analytics";
import Automation from "../pages/Automation";
import Support from "../pages/Support";
import Messages from "../pages/Messages";
import Reports from "../pages/Reports";
import Settings from "../pages/Settings";
import Login from "../pages/Login";
import ActivityLog from "../pages/ActivityLog";
import Admins from "../pages/Admins";
import AdminLogs from "../pages/AdminLogs";
import PlatformConfigs from "../pages/PlatformConfigs";
import CancellationPolicyManager from "../pages/CancellationPolicyManager";
import ContentDocs from "../pages/ContentDocs";
import ReligionData from "../pages/ReligionData";
import PushCampaigns from "../pages/PushCampaigns";
import { getUserRole, getUserDepartment } from "../utils/auth";

// ── Role definitions ─────────────────────────────────────────────
export type AdminRole =
  | "SUPER_ADMIN"
  | "ADMIN"
  | "SUPPORT"
  | "FINANCE"
  | "AD_MANAGER"
  | "MARKETING"
  | "OPERATIONS";

/** Routes each role is permitted to visit (in addition to "/"). */
export const ROLE_ALLOWED_PATHS: Record<string, string[]> = {
  SUPER_ADMIN: ["*"], // all paths
  ADMIN:       ["*"], // all paths
  SUPPORT:     ["/", "/support", "/messages", "/bookings", "/users", "/scholars"],
  FINANCE:     ["/", "/finance", "/finance/payouts", "/analytics", "/reports"],
  AD_MANAGER:  ["/", "/ads", "/push-campaigns"],
  MARKETING:   ["/", "/ads", "/push-campaigns"],
  OPERATIONS:  ["/", "/users", "/scholars", "/bookings", "/services", "/support", "/messages", "/push-campaigns"],
};

/** Returns true when the current role may visit the given path. */
export const canAccess = (role: string | null, department: string | null, path: string): boolean => {
  if (!role) return false;
  if (role === "SUPER_ADMIN") return true;
  if (role === "ADMIN" && !department) return true;

  const effectiveRole = department || role;
  const allowed = ROLE_ALLOWED_PATHS[effectiveRole];
  if (!allowed) return false;
  if (allowed.includes("*")) return true;
  return allowed.some((p) => path === p || path.startsWith(p + "/"));
};

// ── Guards ───────────────────────────────────────────────────────

/** Redirect to login if unauthenticated. */
const PrivateRoute = ({ children }: { children: React.ReactElement }) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

/** Redirect to /unauthorized if the current role cannot access the path. */
const RoleRoute = ({
  children,
  path,
}: {
  children: React.ReactElement;
  path: string;
}) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  const role = getUserRole();
  const department = getUserDepartment();
  if (!canAccess(role, department, path)) return <Navigate to="/unauthorized" replace />;
  return children;
};

// ── Unauthorized page (inline, minimal) ─────────────────────────
const Unauthorized = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      gap: 12,
      color: "hsl(var(--muted-foreground))",
    }}
  >
    <span style={{ fontSize: 48 }}>🔒</span>
    <h2 style={{ margin: 0, fontSize: 22, color: "hsl(var(--foreground))" }}>
      Access Denied
    </h2>
    <p style={{ margin: 0, fontSize: 14 }}>
      You don't have permission to view this page.
    </p>
    <a href="/" style={{ marginTop: 8, fontSize: 13, color: "hsl(var(--primary))" }}>
      ← Back to Dashboard
    </a>
  </div>
);

// ── Routes ───────────────────────────────────────────────────────
const AppRoutes = ({
  isDark = false,
  timeRange = "30d",
}: {
  isDark?: boolean;
  timeRange?: string;
}) => {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/unauthorized" element={<PrivateRoute><Unauthorized /></PrivateRoute>} />

      {/* Dashboard — every authenticated role may see this */}
      <Route path="/" element={<PrivateRoute><Dashboard isDark={isDark} timeRange={timeRange} /></PrivateRoute>} />

      {/* Operations role */}
      <Route path="/users"    element={<RoleRoute path="/users"><Users /></RoleRoute>} />
      <Route path="/scholars" element={<RoleRoute path="/scholars"><Scholars /></RoleRoute>} />
      <Route path="/services" element={<RoleRoute path="/services"><Services /></RoleRoute>} />

      {/* Support role */}
      <Route path="/support"  element={<RoleRoute path="/support"><Support /></RoleRoute>} />
      <Route path="/messages" element={<RoleRoute path="/messages"><Messages /></RoleRoute>} />
      <Route path="/messages/:roomId" element={<RoleRoute path="/messages"><Messages /></RoleRoute>} />

      {/* Shared — Support + Operations */}
      <Route path="/bookings" element={<RoleRoute path="/bookings"><Bookings /></RoleRoute>} />

      {/* Finance role */}
      <Route path="/finance"         element={<RoleRoute path="/finance"><Finance /></RoleRoute>} />
      <Route path="/finance/payouts" element={<RoleRoute path="/finance/payouts"><PayoutsRefunds /></RoleRoute>} />
      <Route path="/analytics"       element={<RoleRoute path="/analytics"><Analytics /></RoleRoute>} />
      <Route path="/reports"         element={<RoleRoute path="/reports"><Reports /></RoleRoute>} />

      {/* Ad Manager / Marketing role */}
      <Route path="/ads" element={<RoleRoute path="/ads"><Advertisements /></RoleRoute>} />
      <Route path="/push-campaigns" element={<RoleRoute path="/push-campaigns"><PushCampaigns /></RoleRoute>} />

      {/* Admin / Super Admin only */}
      <Route path="/automation"  element={<RoleRoute path="/automation"><Automation /></RoleRoute>} />
      <Route path="/settings"    element={<RoleRoute path="/settings"><Settings /></RoleRoute>} />
      <Route path="/activity-log" element={<RoleRoute path="/activity-log"><ActivityLog /></RoleRoute>} />
      <Route path="/admins"      element={<RoleRoute path="/admins"><Admins /></RoleRoute>} />
      <Route path="/admin-logs"  element={<RoleRoute path="/admin-logs"><AdminLogs /></RoleRoute>} />
      <Route path="/platform-configs" element={<RoleRoute path="/platform-configs"><PlatformConfigs /></RoleRoute>} />
      <Route path="/cancellation-policy" element={<RoleRoute path="/cancellation-policy"><CancellationPolicyManager /></RoleRoute>} />
      <Route path="/content-docs"     element={<RoleRoute path="/content-docs"><ContentDocs /></RoleRoute>} />
      <Route path="/religion-data"    element={<RoleRoute path="/religion-data"><ReligionData /></RoleRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
